import { queryClient } from '@/api/queryClient';
import { HOMEPAGE } from '@/config/router';
import { PERMISSION_LIST } from '@/router/permissions';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import userService, { type SignInReq } from '@/api/services/userService';

import { toast } from 'sonner';
import { Role, type UserInfo, type UserToken } from '#/entity';
import { StorageEnum } from '#/enum';

const isProduction = import.meta.env.APP_ENVIRONMENT === 'production';
const ADMIN_LOGIN_ROLES = new Set<string>([
  Role.Admin,
  Role.SuperAdmin,
  ...(isProduction ? [] : [Role.PropertyAdmin]),
]);
const NO_LOGIN_PERMISSION_MESSAGE = 'You do not have permission to log in';

type UserStore = {
  userInfo: Partial<UserInfo>;
  userToken: UserToken;
  actions: {
    setUserInfo: (userInfo: UserInfo) => void;
    setUserToken: (token: UserToken) => void;
    clearUserInfoAndToken: () => void;
  };
};

const useUserStore = create<UserStore>()(
  persist(
    (set) => ({
      userInfo: {},
      userToken: {},
      actions: {
        setUserInfo: (userInfo) => {
          set({ userInfo });
        },
        setUserToken: (userToken) => {
          set({ userToken });
        },
        clearUserInfoAndToken() {
          queryClient.cancelQueries();
          queryClient.clear();
          set({ userInfo: {}, userToken: {} });
        },
      },
    }),
    {
      name: 'userStore',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        [StorageEnum.UserInfo]: state.userInfo,
        [StorageEnum.UserToken]: state.userToken,
      }),
    },
  ),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);

export const useUserPermission = () => {
  const role = useUserInfo().role;

  let userRole: Role = Role.User;
  if (role === Role.SuperAdmin) {
    userRole = Role.SuperAdmin;
  } else if (role === Role.PropertyAdmin) {
    userRole = Role.PropertyAdmin;
  } else if (role === Role.Admin) {
    userRole = Role.Admin;
  }

  const permissions = getPermissionsByRole(userRole);
  return PERMISSION_LIST.map((menu) => filterPermissions(menu, permissions)).filter(Boolean);
};

export const RolePermissionsMap: Record<Role, string[]> = {
  [Role.User]: getAllPermissionRoutesExcept(['admin_logs', 'property_admins']),
  [Role.Admin]: getAllPermissionRoutesExcept(['admin_logs']),
  [Role.PropertyAdmin]: isProduction
    ? []
    : getAllPermissionRoutesExcept(['admin_logs', 'admin_users', 'property_admins']),
  [Role.SuperAdmin]: ['*'],
};

export const getPermissionsByRole = (role: Role): string[] => RolePermissionsMap[role] ?? [];

// 2. Flatten route
function flattenRoutes(menu: any): string[] {
  const routes: string[] = [];
  if (menu.route) routes.push(menu.route);
  if (menu.children?.length) {
    menu.children.forEach((child: any) => {
      routes.push(...flattenRoutes(child));
    });
  }
  return routes;
}

export function getAllPermissionRoutesExcept(exclude: string[]): string[] {
  const allRoutes = PERMISSION_LIST.flatMap(flattenRoutes);
  return allRoutes.filter((route) => !exclude.includes(route));
}

function filterPermissions(menu: any, permissions: string[]): any | null {
  if (!menu.children) {
    return permissions.includes(menu.route) || permissions.includes('*') ? menu : null;
  }

  const filteredChildren = menu.children
    .map((child: any) => filterPermissions(child, permissions))
    .filter(Boolean);

  return filteredChildren.length > 0 ? { ...menu, children: filteredChildren } : null;
}

export const useUserActions = () => useUserStore((state) => state.actions);

export const canLoginToAdmin = (user: Partial<Pick<UserInfo, 'role'>>) =>
  Boolean(user.role && ADMIN_LOGIN_ROLES.has(user.role));

export const showNoLoginPermissionError = () => {
  toast.error(NO_LOGIN_PERMISSION_MESSAGE, {
    position: 'top-center',
  });
};

export const useSignIn = () => {
  const navigate = useNavigate();
  const { setUserToken, setUserInfo, clearUserInfoAndToken } = useUserActions();

  const signInMutation = useMutation({
    mutationFn: userService.signIn,
  });

  const signIn = async (data: SignInReq) => {
    let pendingAccessToken: string | undefined;

    try {
      const { accessToken } = await signInMutation.mutateAsync(data);
      pendingAccessToken = accessToken;

      const user = await userService.getProfile(accessToken);
      if (!canLoginToAdmin(user)) {
        clearUserInfoAndToken();
        showNoLoginPermissionError();
        return;
      }

      setUserToken({ accessToken });
      setUserInfo(user);

      navigate(HOMEPAGE, { replace: true });
      toast.success('Sign in success!', {
        closeButton: true,
      });
    } catch (err: any) {
      clearUserInfoAndToken();
      if (pendingAccessToken) {
        showNoLoginPermissionError();
      }
    }
  };

  return signIn;
};

export default useUserStore;
export const getUserStore = () => useUserStore.getState();
